import { useState } from "react";
import { JOBS, INDUSTRIES } from "../data/jobs";
import { generateAnalysis } from "../data/fallbacks";
import { callClaude } from "../utils/api";
import { NAVY, PANEL, TEAL, CREAM, CREAM60, CREAM30, BORDER, AMBER, GREEN, RED,
         riskColor, riskLabel, statusColor, fmt, fmtWage, sleep } from "../utils/constants";
import PulseLoader from "./PulseLoader";
import JobCard from "./JobCard";

function Phase1({ onSelectJob }) {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");
  const [sortBy, setSortBy] = useState("score");
  const [selJob, setSelJob] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [src, setSrc] = useState(null);

  const filtered = JOBS.filter(j => {
    return (j.title.toLowerCase().includes(search.toLowerCase()) || j.industry.toLowerCase().includes(search.toLowerCase())) && (filter === "All" || j.industry === filter);
  }).sort((a, b) => sortBy === "score" ? b.score - a.score : sortBy === "wage" ? b.wage - a.wage : b.employed - a.employed);

  const analyze = async (job) => {
    setSelJob(job); setAnalysis(null); setSrc(null); setLoading(true);
    try {
      const prompt = `Analyze AI exposure for "${job.title}" in ${job.industry}. Tasks: ${job.tasks.join(", ")}. Score: ${job.score}/10. Return JSON: {"summary":"2 sentences","tasks":[{"name":"task","score":7.5,"status":"automated|augmented|safe","note":"brief"}],"safe_skills":["s1","s2","s3"],"vulnerable_skills":["s1","s2","s3"],"timeline":"Near-term|Mid-term|Long-term","trend":"Accelerating|Steady|Emerging"} Include all 6 tasks.`;
      const [result] = await Promise.all([callClaude(prompt), sleep(1200)]);
      setAnalysis(result); setSrc("ai");
    } catch {
      await sleep(600);
      setAnalysis(generateAnalysis(job)); setSrc("local");
    }
    setLoading(false);
  };

  const sc = statusColor;

  if (selJob) {
    return (
      <div style={{ animation: "fadeUp 0.4s ease" }}>
        <button onClick={() => { setSelJob(null); setAnalysis(null); }}
          style={{ background: "none", border: `1px solid ${CREAM30}`, color: CREAM60, padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13, marginBottom: 20 }}>← Back to all roles</button>
        <div style={{ background: PANEL, border: `1px solid ${BORDER}`, borderTop: `2px solid ${riskColor(selJob.score)}`, borderRadius: 12, padding: 24, marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
            <div>
              <h2 style={{ color: CREAM, margin: 0, fontSize: 22, fontFamily: "'Space Grotesk', sans-serif" }}>{selJob.title}</h2>
              <div style={{ color: CREAM60, fontSize: 13, marginTop: 4 }}>{selJob.industry} · {fmt(selJob.employed)} employed · {fmtWage(selJob.wage)} median</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 36, fontWeight: 700, color: riskColor(selJob.score), fontFamily: "'JetBrains Mono', monospace" }}>{selJob.score}</div>
              <div style={{ fontSize: 12, color: riskColor(selJob.score), fontWeight: 600 }}>{riskLabel(selJob.score)} Exposure</div>
            </div>
          </div>
          <div style={{ height: 6, borderRadius: 3, background: `${CREAM}10`, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${selJob.score * 10}%`, background: `linear-gradient(90deg, ${riskColor(selJob.score)}60, ${riskColor(selJob.score)})`, borderRadius: 3 }} />
          </div>
        </div>
        {loading && <PulseLoader />}
        {analysis && (
          <div style={{ animation: "fadeUp 0.4s ease" }}>
            {src === "local" && <div style={{ background: `${AMBER}10`, border: `1px solid ${AMBER}30`, borderRadius: 8, padding: "8px 14px", marginBottom: 14, fontSize: 12, color: AMBER }}>⚡ Showing research-backed analysis from AI Signals data</div>}
            <div style={{ background: PANEL, border: `1px solid ${BORDER}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: TEAL, fontWeight: 600, marginBottom: 8, textTransform: "uppercase", letterSpacing: 1, fontFamily: "'JetBrains Mono', monospace" }}>AI Risk Summary</div>
              <p style={{ color: CREAM, fontSize: 15, lineHeight: 1.6, margin: 0 }}>{analysis.summary}</p>
            </div>
            <div style={{ fontSize: 12, color: TEAL, fontWeight: 600, marginBottom: 10, textTransform: "uppercase", letterSpacing: 1, fontFamily: "'JetBrains Mono', monospace" }}>Task-Level Exposure</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: 10, marginBottom: 16 }}>
              {(analysis.tasks || []).map((t, i) => (
                <div key={i} style={{ background: `${sc(t.status)}08`, border: `1px solid ${sc(t.status)}30`, borderRadius: 8, padding: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                    <span style={{ color: CREAM, fontSize: 14, fontWeight: 600 }}>{t.name}</span>
                    <span style={{ fontSize: 16, fontWeight: 700, color: sc(t.status), fontFamily: "'JetBrains Mono', monospace" }}>{t.score}</span>
                  </div>
                  <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 3, background: `${sc(t.status)}20`, color: sc(t.status), fontWeight: 600, textTransform: "uppercase" }}>{t.status}</span>
                  <div style={{ height: 3, borderRadius: 2, background: `${CREAM}10`, margin: "8px 0 6px", overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${t.score * 10}%`, background: sc(t.status), borderRadius: 2 }} />
                  </div>
                  <div style={{ color: CREAM60, fontSize: 12, lineHeight: 1.4 }}>{t.note}</div>
                </div>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
              <div style={{ background: PANEL, border: `1px solid ${GREEN}30`, borderRadius: 10, padding: 16 }}>
                <div style={{ fontSize: 12, color: GREEN, fontWeight: 600, marginBottom: 10, fontFamily: "'JetBrains Mono', monospace" }}>SAFE SKILLS</div>
                {(analysis.safe_skills || []).map((s, i) => <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", color: CREAM, fontSize: 13 }}><span style={{ width: 6, height: 6, borderRadius: "50%", background: GREEN, flexShrink: 0 }} />{s}</div>)}
              </div>
              <div style={{ background: PANEL, border: `1px solid ${RED}30`, borderRadius: 10, padding: 16 }}>
                <div style={{ fontSize: 12, color: RED, fontWeight: 600, marginBottom: 10, fontFamily: "'JetBrains Mono', monospace" }}>VULNERABLE SKILLS</div>
                {(analysis.vulnerable_skills || []).map((s, i) => <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", color: CREAM, fontSize: 13 }}><span style={{ width: 6, height: 6, borderRadius: "50%", background: RED, flexShrink: 0 }} />{s}</div>)}
              </div>
            </div>
            <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
              <div style={{ background: PANEL, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "10px 16px", display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ color: CREAM60, fontSize: 12 }}>Timeline</span><span style={{ color: TEAL, fontSize: 13, fontWeight: 600 }}>{analysis.timeline}</span>
              </div>
              <div style={{ background: PANEL, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "10px 16px", display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ color: CREAM60, fontSize: 12 }}>Trend</span><span style={{ color: analysis.trend === "Accelerating" ? RED : analysis.trend === "Steady" ? AMBER : GREEN, fontSize: 13, fontWeight: 600 }}>{analysis.trend}</span>
              </div>
            </div>
            <button onClick={() => onSelectJob(selJob, analysis)} style={{ background: `linear-gradient(135deg, ${TEAL}, #00A3A9)`, color: NAVY, border: "none", padding: "14px 28px", borderRadius: 8, cursor: "pointer", fontSize: 15, fontWeight: 700, width: "100%" }}>Map My Pathways →</button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ animation: "fadeUp 0.4s ease" }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ color: CREAM, fontSize: 22, margin: "0 0 6px", fontFamily: "'Space Grotesk', sans-serif" }}>Explore AI Exposure</h2>
        <p style={{ color: CREAM60, fontSize: 14, margin: 0 }}>30 roles scored by AI disruption risk. Click any role for task-level analysis.</p>
      </div>
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search roles or industries..."
          style={{ flex: 1, minWidth: 180, background: PANEL, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "10px 14px", color: CREAM, fontSize: 14, outline: "none" }} />
        <select value={filter} onChange={e => setFilter(e.target.value)} style={{ background: PANEL, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "10px 14px", color: CREAM, fontSize: 13, outline: "none" }}>
          <option value="All">All Industries</option>
          {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
        </select>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ background: PANEL, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "10px 14px", color: CREAM, fontSize: 13, outline: "none" }}>
          <option value="score">Sort by Risk</option>
          <option value="wage">Sort by Wage</option>
          <option value="employed">Sort by Employment</option>
        </select>
      </div>
      <div style={{ fontSize: 12, color: CREAM30, marginBottom: 12, fontFamily: "'JetBrains Mono', monospace" }}>{filtered.length} roles</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
        {filtered.map((j, i) => <JobCard key={i} job={j} onClick={() => analyze(j)} />)}
      </div>
    </div>
  );
}

export default Phase1;
